import { FulfillmentMethodSelect } from 'components/filter/input/FulfillmentMethodSelect';
import { GenderSelect } from 'components/filter/input/GenderSelect';
import { AgeGroupSelect } from 'components/filter/input/AgeGroupSelect';
import { SeasonSelect } from 'components/filter/input/SeasonSelect';
import { DailyDateRangePicker } from 'components/filter/input/DateRangePicker/DailyDateRangePicker';
import { DAILY_PRESET_REGISTRY, buildPresetOptions } from 'components/filter/input/DateRangePicker/presets';
import { ARTICLE_VISIBILITY_PRESET_IDS } from 'components/filter/modules/article-visibility/presets';
import { INSIGHTS_MAIN_PRESET_IDS, INSIGHTS_COMPARISON_PRESET_IDS } from 'components/filter/modules/insights/presets';
import type { DateValue, PresetOption } from 'components/filter/input/DateRangePicker/types';
import { isValidDateRange } from 'components/filter/input/DateRangePicker/validation';
import { isDateDisabled } from 'components/filter/input/DateRangePicker/validation';
import { DateRangePicker } from 'components/filter/input/DateRangePicker/DateRangePicker';
import { Controller, useFormContext } from 'react-hook-form';
import { BrandSelect } from 'components/filter/input/BrandSelect';
import { MarketplaceSelect } from 'components/filter/input/MarketplaceSelect';
import { CategorySelect } from 'components/filter/input/CategorySelect';
import { CountrySelect } from 'components/filter/input/CountrySelect';
import { ColorSelect } from 'components/filter/input/ColorSelect';
import { SizeSelect } from 'components/filter/input/SizeSelect';
import { AdvancedProductFilter } from 'components/filter/input/AdvancedProductFilter';
import { useIntl } from 'react-intl';
import { BenchmarkSelect } from 'components/filter/input/BenchmarkSelect';
import { AdvancedFilterData } from '../FilterFormController';
import { FilterItem } from '../types';
import { useMemo } from 'react';

interface ControlledDateRangeFieldProps {
  value: DateValue;
  onChange: (dv: DateValue) => void;
  caption: string;
  emptyMessage?: string;
  isComparisonMode?: boolean;
}

/**
 * Form-controlled date range field using DailyDateRangePicker.
 * Handles both main and comparison modes.
 */
function ControlledDateRangeField({
  value,
  onChange,
  caption,
  emptyMessage = 'datepicker.empty.default',
  isComparisonMode = false,
}: ControlledDateRangeFieldProps) {
  return (
    <DailyDateRangePicker
      value={value}
      onChange={onChange}
      presetIds={isComparisonMode ? INSIGHTS_COMPARISON_PRESET_IDS : INSIGHTS_MAIN_PRESET_IDS}
      comparisonPresetIds={isComparisonMode ? undefined : INSIGHTS_COMPARISON_PRESET_IDS}
      isComparison={isComparisonMode}
      caption={caption}
      emptyMessage={emptyMessage}
      hideButtons
      fullWidth
    />
  );
}

/**
 * Form-controlled creation date field for article visibility.
 * Uses a different set of presets and doesn't support comparison.
 */
function ControlledCreationDateField({ value, onChange }: { value: DateValue; onChange: (dv: DateValue) => void }) {
  const intl = useIntl();
  const today = useMemo(() => new Date(), []);

  const presetOptions = useMemo<PresetOption[]>(
    () => buildPresetOptions(ARTICLE_VISIBILITY_PRESET_IDS, DAILY_PRESET_REGISTRY, today),
    [today]
  );

  return (
    <DateRangePicker
      granularity="daily"
      caption="filter.key.selectedCreationDateRange"
      emptyMessage="datepicker.empty.no.filtering"
      presetOptions={presetOptions}
      onValidate={(startDate, endDate) => isValidDateRange(startDate, endDate, false, intl)}
      disabled={false}
      disabledDates={isDateDisabled}
      value={{ range: value.range }}
      onChange={dv => onChange({ range: dv.range })}
      isComparison={false}
      hideButtons
      hideDateInputs={!presetOptions.some(p => p.value === 'custom')}
      fullWidth
    />
  );
}

function RenderFilterItem(item: FilterItem, index: number) {
  const { cols = 1, type } = item;
  const colSpanClass = cols === 2 ? 'col-span-2' : 'col-span-1';
  const key = `${type}-${index}`;
  const { control, setValue } = useFormContext<AdvancedFilterData>();
  const intl = useIntl();

  switch (type) {
    case 'dateRange':
      return (
        <div key={key} className={`${colSpanClass} w-full`}>
          <Controller
            name="selectedDateRange"
            control={control}
            render={({ field }) => (
              <ControlledDateRangeField
                value={field.value}
                onChange={dv => setValue('selectedDateRange', dv)}
                caption="filter.key.selectedDateRange"
              />
            )}
          />
        </div>
      );

    case 'dateRangeComparison':
      return (
        <div key={key} className={`${colSpanClass} w-full`}>
          <Controller
            name="selectedDateRange"
            control={control}
            render={({ field }) => (
              <ControlledDateRangeField
                value={field.value}
                onChange={dv => setValue('selectedDateRange', dv)}
                caption="filter.key.selectedDateRangeComparison"
                isComparisonMode
              />
            )}
          />
        </div>
      );

    case 'creationDateRange':
      return (
        <div key={key} className={`${colSpanClass} w-full`}>
          <Controller
            name="selectedCreationDateRange"
            control={control}
            render={({ field }) => (
              <ControlledCreationDateField
                value={field.value}
                onChange={dv => setValue('selectedCreationDateRange', dv)}
              />
            )}
          />
        </div>
      );

    case 'brands':
      return (
        <div key={key} className={`${colSpanClass} w-full`}>
          <Controller
            name="selectedBrands"
            control={control}
            render={({ field }) => (
              <BrandSelect
                setSelectedBrands={dv => setValue('selectedBrands', dv)}
                selectedBrands={field.value}
                className="w-full"
                showConfirmButton
              />
            )}
          />
        </div>
      );

    case 'marketplaces':
      return (
        <div key={key} className={`${colSpanClass} w-full`}>
          <Controller
            name="selectedMarketplaces"
            control={control}
            render={({ field }) => (
              <MarketplaceSelect
                setSelectedMarketplaces={dv => setValue('selectedMarketplaces', dv)}
                selectedMarketplaces={field.value}
                className="w-full"
                showConfirmButton
              />
            )}
          />
        </div>
      );

    case 'countries':
      return (
        <div key={key} className={`${colSpanClass} w-full`}>
          <Controller
            name="selectedCountries"
            control={control}
            render={({ field }) => (
              <CountrySelect
                setSelectedCountries={dv => setValue('selectedCountries', dv)}
                selectedCountries={field.value}
                className="w-full"
                showConfirmButton
              />
            )}
          />
        </div>
      );

    case 'categories':
      return (
        <div key={key} className={`${colSpanClass} w-full`}>
          <Controller
            name="selectedCategories"
            control={control}
            render={({ field }) => (
              <CategorySelect
                setSelectedCategories={dv => setValue('selectedCategories', dv)}
                selectedCategories={field.value}
                className="w-full"
                showConfirmButton
              />
            )}
          />
        </div>
      );

    case 'seasons':
      return (
        <div key={key} className={`${colSpanClass} w-full`}>
          <Controller
            name="selectedSeasons"
            control={control}
            render={({ field }) => (
              <SeasonSelect
                setSelectedSeasons={dv => setValue('selectedSeasons', dv)}
                selectedSeasons={field.value}
                className="w-full"
                showConfirmButton
              />
            )}
          />
        </div>
      );

    case 'genders':
      return (
        <div key={key} className={`${colSpanClass} w-full`}>
          <Controller
            name="selectedGenders"
            control={control}
            render={({ field }) => (
              <GenderSelect
                setSelectedGenders={dv => setValue('selectedGenders', dv)}
                selectedGenders={field.value}
                className="w-full"
                showConfirmButton
              />
            )}
          />
        </div>
      );

    case 'ageGroups':
      return (
        <div key={key} className={`${colSpanClass} w-full`}>
          <Controller
            name="selectedAgeGroups"
            control={control}
            render={({ field }) => (
              <AgeGroupSelect
                setSelectedAgeGroups={dv => setValue('selectedAgeGroups', dv)}
                selectedAgeGroups={field.value}
                className="w-full"
                showConfirmButton
              />
            )}
          />
        </div>
      );

    case 'fulfillmentMethods':
      return (
        <div key={key} className={`${colSpanClass} w-full`}>
          <Controller
            name="selectedFulfillmentMethods"
            control={control}
            render={({ field }) => (
              <FulfillmentMethodSelect
                setSelectedFulfillmentMethods={dv => setValue('selectedFulfillmentMethods', dv)}
                selectedFulfillmentMethods={field.value}
                className="w-full"
                showConfirmationButton
              />
            )}
          />
        </div>
      );

    case 'products':
      return (
        <div key={key} className={`${colSpanClass} w-full`}>
          <Controller
            name="selectedProducts"
            control={control}
            render={({ field }) => (
              <AdvancedProductFilter
                selectedProductNumbers={field.value}
                setSelectedProductNumbers={dv => setValue('selectedProducts', dv)}
                placeholder={intl.formatMessage({
                  id: 'products.advanced.search_placeholder',
                  defaultMessage: 'Please enter Product Name or Product Number',
                })}
              />
            )}
          />
        </div>
      );

    case 'benchmark':
      return (
        <div key={key} className={`${colSpanClass} w-full`}>
          <Controller
            name="selectedBenchmark"
            control={control}
            render={({ field }) => (
              <BenchmarkSelect
                selectedBenchmark={field.value}
                setSelectedBenchmark={dv => setValue('selectedBenchmark', dv)}
                className="w-full"
              />
            )}
          />
        </div>
      );

    case 'colors':
      return (
        <div key={key} className={`${colSpanClass} w-full`}>
          <Controller
            name="selectedColors"
            control={control}
            render={({ field }) => (
              <ColorSelect
                selectedColors={field.value}
                setSelectedColors={dv => setValue('selectedColors', dv)}
                className="w-full"
                showConfirmButton
              />
            )}
          />
        </div>
      );

    case 'sizes':
      return (
        <div key={key} className={`${colSpanClass} w-full`}>
          <Controller
            name="selectedSizes"
            control={control}
            render={({ field }) => (
              <SizeSelect
                selectedSizes={field.value}
                setSizes={dv => setValue('selectedSizes', dv)}
                className="w-full"
                showConfirmButton
              />
            )}
          />
        </div>
      );

    default:
      return null;
  }
}

export type { FilterItem };
export { RenderFilterItem };
